<div class="optimizer-lab-page">
  <!-- Loading state -->
  <div *ngIf="loading" class="loading-state">
    <p>Loading Optimizer Lab...</p>
  </div>

  <!-- Main content -->
  <div *ngIf="!loading" class="lab-content">
    <header class="lab-header">
      <h1>Observe Mode</h1>
      <p class="subtitle">Preview projected savings without making provider calls. No API keys required.</p>
      <div class="no-provider-badge">
        <mat-icon class="badge-icon" aria-hidden="true">visibility</mat-icon>
        <span>No provider call made • No prompt uploaded to Spectyra cloud • Savings are projected</span>
      </div>
      <div class="security-badges" style="display:flex;flex-wrap:wrap;gap:6px;margin-top:10px">
        <span style="display:inline-flex;align-items:center;background:rgba(52,211,153,.1);color:#059669;padding:3px 10px;border-radius:4px;font-size:12px;font-weight:500">Inference: None (dry-run)</span>
        <span style="display:inline-flex;align-items:center;background:rgba(52,211,153,.1);color:#059669;padding:3px 10px;border-radius:4px;font-size:12px;font-weight:500">Telemetry: Local</span>
        <span style="display:inline-flex;align-items:center;background:rgba(52,211,153,.1);color:#059669;padding:3px 10px;border-radius:4px;font-size:12px;font-weight:500">Billing: Not applicable</span>
        <span style="display:inline-flex;align-items:center;background:rgba(52,211,153,.1);color:#059669;padding:3px 10px;border-radius:4px;font-size:12px;font-weight:500">Cloud relay: None</span>
      </div>
    </header>

    <div class="lab-layout">
      <!-- Left: Input Panel -->
      <section class="input-panel">
        <div class="panel-header">
          <h2>Input</h2>
        </div>

        <div class="form-section">
          <!-- Demo Type: tells the optimizer and generator what kind of content this is -->
          <div class="form-group">
            <label>Demo Type</label>
            <div class="button-group">
              <button 
                [class.active]="demoType === 'chat'"
                (click)="demoType = 'chat'">
                <mat-icon class="btn-icon">chat_bubble_outline</mat-icon>
                Chat
              </button>
              <button 
                [class.active]="demoType === 'code'"
                (click)="demoType = 'code'">
                <mat-icon class="btn-icon">code</mat-icon>
                Code
              </button>
            </div>
            <small class="hint">Chat = support/sales style. Code = agentic coding sessions. Choose based on your content.</small>
          </div>

          <!-- Optimization Level -->
          <div class="form-group">
            <label>Optimization Level</label>
            <div class="button-group level-group">
              <button 
                [class.active]="optimizationLevel === 'safe'"
                (click)="optimizationLevel = 'safe'"
                title="Conservative optimization, minimal changes">
                Safe
              </button>
              <button 
                [class.active]="optimizationLevel === 'balanced'"
                (click)="optimizationLevel = 'balanced'"
                title="Balanced optimization">
                Balanced
              </button>
              <button 
                [class.active]="optimizationLevel === 'aggressive'"
                (click)="optimizationLevel = 'aggressive'"
                title="Maximum optimization">
                Aggressive
              </button>
            </div>
          </div>

          <!-- View Mode -->
          <div class="form-group">
            <label>View Mode</label>
            <select [(ngModel)]="viewMode" class="form-select">
              <option value="DEMO_VIEW">Demo View (Redacted)</option>
              <option value="ADMIN_DEBUG">Admin Debug (Full)</option>
              <option value="FORENSICS">Forensics (Full + Extra)</option>
            </select>
          </div>

          <!-- Include Debug -->
          <div class="form-group checkbox-group">
            <label>
              <input type="checkbox" [(ngModel)]="includeDebug">
              Include debug info
            </label>
          </div>

          <!-- Presets: load a ready-made sample into the Prompt field -->
          <div class="form-group">
            <label>Load Preset</label>
            <div class="button-group preset-group">
              <button class="btn-secondary" (click)="loadPreset('chat')">
                <mat-icon class="btn-icon">content_paste</mat-icon>
                Chat Demo
              </button>
              <button class="btn-secondary" (click)="loadPreset('code')">
                <mat-icon class="btn-icon">content_paste</mat-icon>
                Code Demo
              </button>
            </div>
            <small class="hint">Load a sample prompt into the input below. Or use Generate Test Data for synthetic messages.</small>
          </div>

          <!-- Generate Test Data (uses Demo Type above) -->
          <div class="form-section generate-section">
            <h3 class="generate-panel-title">Generate Test Data</h3>
            <div class="form-group">
              <label>Turn Count</label>
              <div class="turn-count-row">
                <input type="number" [(ngModel)]="turnCount" class="form-input turn-input" min="1" max="500" placeholder="8">
                <div class="quick-turns">
                  <button type="button" class="btn-quick" (click)="turnCount = 8">8</button>
                  <button type="button" class="btn-quick" (click)="turnCount = 12">12</button>
                  <button type="button" class="btn-quick" (click)="turnCount = 20">20</button>
                </div>
              </div>
              <small class="hint">8–12 turns is typical. Higher counts are stress tests and can exaggerate savings.</small>
            </div>
            <div class="form-group">
              <label>Seed</label>
              <div class="seed-row">
                <input type="number" [(ngModel)]="seed" class="form-input seed-input" min="0" placeholder="42">
                <button type="button" class="btn-secondary" (click)="randomizeSeed()">Randomize</button>
              </div>
            </div>
            <div class="form-group">
              <label>Scenario</label>
              <select [(ngModel)]="generatorScenario" class="form-select">
                <option *ngFor="let opt of generatorScenarioOptions" [value]="opt">{{ opt }}</option>
              </select>
              <small class="hint">Change scenario then click &quot;Generate Messages&quot; to refresh prompt data.</small>
            </div>
            <div class="form-group checkbox-group">
              <label>
                <input type="checkbox" [(ngModel)]="includeSystemMessage">
                Include system message
              </label>
            </div>
            <div *ngIf="demoType === 'code'" class="form-group checkbox-group">
              <label>
                <input type="checkbox" [(ngModel)]="includeToolTraces">
                Include tool traces (code only)
              </label>
            </div>
            <div class="generate-buttons">
              <button type="button" class="btn-primary" (click)="generateMessages()">Generate Messages</button>
            </div>
            <small class="hint">Then click Run Optimization below when ready.</small>
            <p *ngIf="generatedSummary" class="generated-summary">{{ generatedSummary }}</p>
          </div>
        </div>

        <hr class="divider">

        <!-- Prompt Input -->
        <div class="form-section">
          <div class="form-group">
            <label>Prompt</label>
            <textarea 
              [(ngModel)]="prompt" 
              class="form-textarea"
              rows="8"
              placeholder="Enter your prompt here, or load a preset above..."></textarea>
          </div>

          <!-- Repo Context (code only) -->
          <div *ngIf="demoType === 'code'" class="form-group">
            <label>Repository Context</label>
            <textarea 
              [(ngModel)]="repoContext" 
              class="form-textarea code-textarea"
              rows="6"
              placeholder="Paste file contents, directory structure, or build logs..."></textarea>
          </div>
        </div>

        <!-- Advanced Options (collapsible) -->
        <div class="form-section advanced-section">
          <button 
            class="toggle-advanced"
            (click)="showAdvancedOptions = !showAdvancedOptions">
            <mat-icon class="toggle-icon" aria-hidden="true">{{ showAdvancedOptions ? 'expand_less' : 'keyboard_arrow_right' }}</mat-icon>
            Advanced Options
          </button>
          
          <div *ngIf="showAdvancedOptions" class="advanced-options">
            <div class="form-group">
              <label>Messages (JSON)</label>
              <textarea 
                [(ngModel)]="advancedMessages" 
                class="form-textarea code-textarea"
                rows="4"
                placeholder='[{"role": "user", "content": "..."}]'></textarea>
              <small class="hint">Override prompt with raw ChatMessage[] array</small>
            </div>
            
            <div class="options-grid">
              <div class="form-group">
                <label>Keep Last Turns</label>
                <input 
                  type="number" 
                  [(ngModel)]="keepLastTurns" 
                  class="form-input"
                  min="1" 
                  max="20"
                  placeholder="Auto">
              </div>
              <div class="form-group">
                <label>Max Refs</label>
                <input 
                  type="number" 
                  [(ngModel)]="maxRefs" 
                  class="form-input"
                  min="1" 
                  max="50"
                  placeholder="Auto">
              </div>
            </div>
          </div>
        </div>

        <p *ngIf="showLowTurnWarning" class="low-turn-warning">
          RefPack/PhraseBook benefits appear with repeated turns. Generate 20+ turns for better demo results.
        </p>

        <!-- Run Button -->
        <div class="action-buttons">
          <button 
            class="btn-primary run-btn"
            (click)="runOptimization()"
            [disabled]="running">
            <mat-icon class="btn-icon" *ngIf="!running">play_arrow</mat-icon>
            {{ running ? 'Running...' : 'Run Optimization' }}
          </button>
          <button 
            *ngIf="result"
            class="btn-secondary"
            (click)="clearResults()">
            <mat-icon class="btn-icon">clear</mat-icon>
            Clear
          </button>
        </div>

        <!-- Error -->
        <div *ngIf="error" class="error-message">
          {{ error }}
        </div>
      </section>

      <!-- Right: Results Panel -->
      <section class="results-panel" [class.has-results]="result">
        <div *ngIf="!result && !running" class="empty-state">
          <mat-icon class="empty-icon" aria-hidden="true">science</mat-icon>
          <p>Run an optimization to see results</p>
          <p class="hint">Choose a demo type, load a preset, or enter your own prompt</p>
        </div>

        <div *ngIf="running" class="loading-state">
          <div class="spinner"></div>
          <p>Running optimization pipeline...</p>
        </div>

        <div *ngIf="result" class="results-content">
          <!-- Tabs -->
          <div class="tabs" role="tablist">
            <button type="button" role="tab"
              [class.active]="activeTab === 'metrics'"
              [attr.aria-selected]="activeTab === 'metrics'"
              (click)="switchTab('metrics')">
              <mat-icon class="tab-icon">bar_chart</mat-icon>
              Metrics
            </button>
            <button type="button" role="tab"
              [class.active]="activeTab === 'trust'"
              [attr.aria-selected]="activeTab === 'trust'"
              (click)="switchTab('trust')">
              <mat-icon class="tab-icon">shield</mat-icon>
              Trust
            </button>
            <button type="button" role="tab"
              [class.active]="activeTab === 'before'"
              [attr.aria-selected]="activeTab === 'before'"
              (click)="switchTab('before')">
              <mat-icon class="tab-icon">history</mat-icon>
              Before
            </button>
            <button type="button" role="tab"
              [class.active]="activeTab === 'after'"
              [attr.aria-selected]="activeTab === 'after'"
              (click)="switchTab('after')">
              <mat-icon class="tab-icon">check_circle_outline</mat-icon>
              After
            </button>
            <button type="button" role="tab"
              [class.active]="activeTab === 'diff'"
              [attr.aria-selected]="activeTab === 'diff'"
              (click)="switchTab('diff')">
              <mat-icon class="tab-icon">compare_arrows</mat-icon>
              Diff
            </button>
            <button type="button" role="tab"
              *ngIf="result.debug"
              [class.active]="activeTab === 'debug'"
              [attr.aria-selected]="activeTab === 'debug'"
              (click)="switchTab('debug')">
              <mat-icon class="tab-icon">build</mat-icon>
              Debug
            </button>
          </div>

          <!-- Tab Content -->
          <div class="tab-content" role="tabpanel">
            <!-- Metrics Tab -->
            <div *ngIf="activeTab === 'metrics'" class="tab-pane metrics-pane">
              <div class="savings-hero" [class.no-savings]="!hasSavings">
                <div class="savings-value">{{ savingsHeroValue }}</div>
                <div class="savings-label">{{ savingsHeroLabel }}</div>
              </div>

              <div class="metrics-grid">
                <div class="metric-card">
                  <div class="metric-label">Input Tokens Before</div>
                  <div class="metric-value">{{ formatNumber(result.diff.summary.inputTokensBefore) }}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-label">Input Tokens After</div>
                  <div class="metric-value success">{{ formatNumber(result.diff.summary.inputTokensAfter) }}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-label">Est. Cost Before</div>
                  <div class="metric-value">{{ formatCost(result.original.tokenEstimate.estimatedCostUsd) }}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-label">Est. Cost After</div>
                  <div class="metric-value success">{{ formatCost(result.optimized.tokenEstimate.estimatedCostUsd) }}</div>
                </div>
                <div class="metric-card">
                  <div class="metric-label">Latency</div>
                  <div class="metric-value">{{ result.meta.latencyMs }}ms</div>
                </div>
                <div class="metric-card">
                  <div class="metric-label">View Mode</div>
                  <div class="metric-value">{{ result.viewMode }}</div>
                </div>
              </div>

              <div class="scc-panels-section">
                <h3>PG-SCC (State Compiler)</h3>
                <div class="scc-panels-grid">
                  <div class="scc-panel">
                    <span class="scc-panel-label">SCC State size</span>
                    <span class="scc-panel-value">{{ result.diff.summary.sccStateChars ?? '—' }} chars</span>
                  </div>
                  <div class="scc-panel">
                    <span class="scc-panel-label">Dropped turns</span>
                    <span class="scc-panel-value">{{ result.diff.summary.sccDroppedTurns ?? '—' }}</span>
                  </div>
                  <div class="scc-panel" *ngIf="result.meta.demoType === 'code'">
                    <span class="scc-panel-label">Failing signals count</span>
                    <span class="scc-panel-value">{{ result.diff.summary.failingSignalsCount ?? '—' }}</span>
                  </div>
                  <div class="scc-panel">
                    <span class="scc-panel-label">λ₂ → aggressiveness</span>
                    <span class="scc-panel-value">{{ result.diff.summary.lambda2 != null ? (result.diff.summary.lambda2 | number:'1.3-3') : '—' }}</span>
                  </div>
                  <div class="scc-panel">
                    <span class="scc-panel-label">Tokens before / after</span>
                    <span class="scc-panel-value">{{ formatNumber(result.diff.summary.inputTokensBefore) }} / {{ formatNumber(result.diff.summary.inputTokensAfter) }}</span>
                  </div>
                </div>
              </div>

              <div class="transforms-section">
                <h3>Applied Transforms</h3>
                <div class="transforms-list">
                  <span 
                    *ngFor="let transform of result.diff.appliedTransforms"
                    class="transform-badge">
                    {{ transform }}
                  </span>
                  <span *ngIf="!(result.diff.appliedTransforms.length)" class="no-transforms">
                    No transforms applied
                  </span>
                </div>
              </div>

              <div class="why-savings-section">
                <h3>{{ hasSavings ? 'Why savings happened' : 'Token change' }}</h3>
                <ul class="why-savings-list">
                  <li>
                    <strong>{{ hasSavings ? 'Tokens removed:' : 'Tokens change:' }}</strong>
                    {{ tokensChangeDescription }}
                  </li>
                  <li *ngIf="result.diff.summary.refsUsed != null">
                    <strong>RefPack entries:</strong> {{ result.diff.summary.refsUsed }}
                    (repeated blocks replaced by refs)
                  </li>
                  <li *ngIf="result.diff.summary.phrasebookEntries != null">
                    <strong>PhraseBook entries:</strong> {{ result.diff.summary.phrasebookEntries }}
                    (repeated phrases encoded)
                  </li>
                  <li *ngIf="result.meta.demoType === 'code'">
                    <strong>CodeMap:</strong>
                    <span *ngIf="result.diff.summary.codemapSnippetsKept != null">{{ result.diff.summary.codemapSnippetsKept }} snippets kept</span>
                    <span *ngIf="result.diff.summary.codemapOmittedBlocks != null">, {{ result.diff.summary.codemapOmittedBlocks }} blocks omitted</span>
                    <span *ngIf="result.diff.summary.codemapSnippetsKept == null && result.diff.summary.codemapOmittedBlocks == null">—</span>
                  </li>
                </ul>
              </div>

              <div *ngIf="result.diff.summary.optimizationSteps?.length" class="per-layer-section">
                <h4>Per-layer (profit gates)</h4>
                <ul class="per-layer-list">
                  <li *ngFor="let step of result.diff.summary.optimizationSteps" [class.skipped]="!step.useAfter">
                    <strong>{{ step.label }}:</strong>
                    {{ step.before }} → {{ step.after }} tokens
                    ({{ step.pct >= 0 ? '+' : '' }}{{ step.pct.toFixed(1) }}%)
                    <span *ngIf="!step.useAfter" class="skipped-badge">skipped (did not meet gate)</span>
                  </li>
                </ul>
              </div>

              <div *ngIf="result.diff.summary.refsUsed != null" class="detail-row">
                <span class="detail-label">RefPack Entries:</span>
                <span class="detail-value">{{ result.diff.summary.refsUsed }}</span>
              </div>
              <div *ngIf="result.diff.summary.phrasebookEntries != null" class="detail-row">
                <span class="detail-label">Phrasebook Entries:</span>
                <span class="detail-value">{{ result.diff.summary.phrasebookEntries }}</span>
              </div>
              <div *ngIf="result.diff.summary.codemapSnippetsKept != null" class="detail-row">
                <span class="detail-label">CodeMap Symbols:</span>
                <span class="detail-value">{{ result.diff.summary.codemapSnippetsKept }}</span>
              </div>

              <div class="export-section">
                <button class="btn-secondary" (click)="exportRedactedReport()">
                  <mat-icon class="btn-icon">file_download</mat-icon>
                  Export Redacted Report
                </button>
              </div>
            </div>

            <!-- Trust Tab -->
            <div *ngIf="activeTab === 'trust'" class="tab-pane trust-pane">
              <div class="trust-intro">
                <h3><mat-icon class="heading-icon">shield</mat-icon> Trust & Safety Summary</h3>
                <p>Spectyra optimizes prompts without losing important content. Here's what we preserved and what we changed.</p>
              </div>

              <div class="trust-section preserved-section">
                <h4><mat-icon class="list-icon success">check_circle</mat-icon> Preserved (Unchanged)</h4>
                <ul>
                  <li *ngFor="let item of (result.diff.safetySummary.preserved ?? [])">{{ item }}</li>
                </ul>
              </div>

              <div class="trust-section changed-section">
                <h4><mat-icon class="list-icon">sync</mat-icon> Changed (Optimized)</h4>
                <ul>
                  <li *ngFor="let item of (result.diff.safetySummary.changed ?? [])">{{ item }}</li>
                </ul>
                <p *ngIf="!(result.diff.safetySummary.changed?.length)" class="no-items">
                  No changes applied
                </p>
              </div>

              <div class="trust-section risk-section">
                <h4><mat-icon class="list-icon">info</mat-icon> Important Notes</h4>
                <ul>
                  <li *ngFor="let item of (result.diff.safetySummary.riskNotes ?? [])">{{ item }}</li>
                </ul>
              </div>
            </div>

            <!-- Before Tab -->
            <div *ngIf="activeTab === 'before'" class="tab-pane content-pane">
              <div class="content-header">
                <h3>Before: Raw conversation history (what you’d send without Spectyra)</h3>
                <button 
                  class="btn-sm"
                  (click)="copyToClipboard(getRenderedText(result.original.renderedText))">
                  <mat-icon class="btn-icon-sm">content_copy</mat-icon>
                  Copy
                </button>
              </div>
              <p class="hint" style="margin: 0 0 10px 0;">
                This is the full message list (user/assistant/tool) that many SDKs resend on each request to keep context.
              </p>
              <div class="message-list">
                <div 
                  *ngFor="let msg of (result.original.messages ?? [])"
                  class="message-item"
                  [class.system]="msg.role === 'system'"
                  [class.user]="msg.role === 'user'"
                  [class.assistant]="msg.role === 'assistant'"
                  [class.tool]="msg.role === 'tool'">
                  <div class="message-role">{{ msg.role | uppercase }}</div>
                  <pre class="message-content">{{ msg.content }}</pre>
                </div>
              </div>
            </div>

            <!-- After Tab -->
            <div *ngIf="activeTab === 'after'" class="tab-pane content-pane">
              <div class="content-header">
                <h3>After: Compiled next-call prompt (what Spectyra would send)</h3>
                <button 
                  *ngIf="result.viewMode !== 'DEMO_VIEW'"
                  class="btn-sm"
                  (click)="copyToClipboard(getRenderedText(result.optimized.renderedText))">
                  <mat-icon class="btn-icon-sm">content_copy</mat-icon>
                  Copy
                </button>
                <span *ngIf="result.viewMode === 'DEMO_VIEW'" class="copy-disabled">
                  Copy disabled (redacted)
                </span>
              </div>
              <p class="hint" style="margin: 0 0 10px 0;">
                Spectyra compiles prior turns into a compact state (often inside the system message), so the optimized message list may shrink to just 1–2 messages.
                This represents the prompt for the <b>next</b> model call, not a rewritten transcript of the entire chat.
              </p>
              
              <div *ngIf="isRedacted(result.optimized.messages)" class="redacted-notice">
                <mat-icon class="redacted-icon" aria-hidden="true">lock</mat-icon>
                <span>Content redacted for IP protection in DEMO_VIEW mode</span>
              </div>
              
              <div class="message-list">
                <div 
                  *ngFor="let msg of getMessages(result.optimized.messages ?? [])"
                  class="message-item"
                  [class.system]="msg.role === 'system'"
                  [class.user]="msg.role === 'user'"
                  [class.assistant]="msg.role === 'assistant'"
                  [class.tool]="msg.role === 'tool'">
                  <div class="message-role">{{ msg.role | uppercase }}</div>
                  <pre class="message-content">{{ msg.content }}</pre>
                </div>
              </div>
            </div>

            <!-- Diff Tab -->
            <div *ngIf="activeTab === 'diff'" class="tab-pane diff-pane">
              <div class="content-header">
                <h3>Diff View</h3>
              </div>
              
              <div *ngIf="result.viewMode === 'DEMO_VIEW'" class="diff-summary-only">
                <p>Detailed diff is only available in ADMIN_DEBUG or FORENSICS mode.</p>
                <div class="diff-summary">
                  <div class="diff-stat">
                    <span class="stat-label">{{ tokensChange >= 0 ? 'Tokens removed:' : 'Tokens added:' }}</span>
                    <span class="stat-value" [class.removed]="tokensChange > 0" [class.added]="tokensChange < 0">
                      {{ tokensChange >= 0 ? formatNumber(tokensChange) : formatNumber(-tokensChange) }}
                    </span>
                  </div>
                  <div class="diff-stat">
                    <span class="stat-label">Savings:</span>
                    <span class="stat-value" [class.success]="hasSavings">{{ hasSavings ? formatPercent(result.diff.summary.pctSaved) : '—' }}</span>
                  </div>
                </div>
              </div>
              
              <div *ngIf="result.diff.unifiedDiff" class="unified-diff">
                <pre>{{ result.diff.unifiedDiff }}</pre>
              </div>
            </div>

            <!-- Debug Tab -->
            <div *ngIf="activeTab === 'debug'" class="tab-pane debug-pane">
              <div class="content-header">
                <h3>Debug Information</h3>
                <span class="internal-badge">Internal Only</span>
              </div>

              <div *ngIf="result.debug?.spectral" class="debug-section">
                <h4>Spectral Analysis</h4>
                <div class="debug-grid">
                  <div class="debug-item">
                    <span class="debug-label">Nodes:</span>
                    <span class="debug-value">{{ result.debug?.spectral?.nNodes ?? '—' }}</span>
                  </div>
                  <div class="debug-item">
                    <span class="debug-label">Edges:</span>
                    <span class="debug-value">{{ result.debug?.spectral?.nEdges ?? '—' }}</span>
                  </div>
                  <div class="debug-item">
                    <span class="debug-label">Stability Index:</span>
                    <span class="debug-value">{{ result.debug?.spectral?.stabilityIndex != null ? (result.debug?.spectral?.stabilityIndex | number:'1.3-3') : '—' }}</span>
                  </div>
                  <div class="debug-item">
                    <span class="debug-label">Lambda2:</span>
                    <span class="debug-value">{{ result.debug?.spectral?.lambda2 != null ? (result.debug?.spectral?.lambda2 | number:'1.3-3') : 'N/A' }}</span>
                  </div>
                  <div class="debug-item">
                    <span class="debug-label">Recommendation:</span>
                    <span class="debug-value recommendation">{{ result.debug?.spectral?.recommendation ?? '—' }}</span>
                  </div>
                  <div class="debug-item">
                    <span class="debug-label">Stable/Unstable:</span>
                    <span class="debug-value">{{ result.debug?.spectral?.stableCount ?? '—' }}/{{ result.debug?.spectral?.unstableCount ?? '—' }}</span>
                  </div>
                </div>
              </div>

              <div *ngIf="result.debug?.budgets" class="debug-section">
                <h4>Computed Budgets</h4>
                <div class="debug-grid">
                  <div class="debug-item">
                    <span class="debug-label">Keep Last Turns:</span>
                    <span class="debug-value">{{ result.debug?.budgets?.keepLastTurns ?? '—' }}</span>
                  </div>
                  <div class="debug-item">
                    <span class="debug-label">Max RefPack Entries:</span>
                    <span class="debug-value">{{ result.debug?.budgets?.maxRefpackEntries ?? '—' }}</span>
                  </div>
                  <div class="debug-item">
                    <span class="debug-label">Compression:</span>
                    <span class="debug-value">{{ formatDebugPercent(result.debug?.budgets?.compressionAggressiveness) }}</span>
                  </div>
                  <div class="debug-item">
                    <span class="debug-label">Phrasebook:</span>
                    <span class="debug-value">{{ formatDebugPercent(result.debug?.budgets?.phrasebookAggressiveness) }}</span>
                  </div>
                  <div class="debug-item">
                    <span class="debug-label">CodeMap Detail:</span>
                    <span class="debug-value">{{ formatDebugPercent(result.debug?.budgets?.codemapDetailLevel) }}</span>
                  </div>
                </div>
              </div>

              <div *ngIf="result.debug?.transforms" class="debug-section">
                <h4>Transform Metrics</h4>
                <pre class="debug-json">{{ result.debug?.transforms | json }}</pre>
              </div>

              <div *ngIf="result.debug?.runDebug" class="debug-section">
                <h4>Run Debug</h4>
                <pre class="debug-json">{{ result.debug?.runDebug | json }}</pre>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</div>
